import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  X,
  Package,
  Printer,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import { printHtmlInSameTab } from '../Reports/reportPrintHelpers';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const parseMonthResponse = (response) => ({
  month: Number(response?.Month ?? response?.month ?? 0),
  year: Number(response?.Year ?? response?.year ?? 0),
  monthTotal: Number(response?.MonthTotal ?? response?.monthTotal ?? 0),
  days: Array.isArray(response?.Days) ? response.Days : (Array.isArray(response?.days) ? response.days : []),
});

const parseDay = (day) => ({
  day: Number(day?.Day ?? day?.day ?? 0),
  month: Number(day?.Month ?? day?.month ?? 0),
  year: Number(day?.Year ?? day?.year ?? 0),
  totalOrders: Number(day?.TotalOrders ?? day?.totalOrders ?? 0),
  statusCounts: (Array.isArray(day?.StatusCounts) ? day.StatusCounts : (Array.isArray(day?.statusCounts) ? day.statusCounts : []))
    .map((item) => ({
      statusId: Number(item?.StatusId ?? item?.statusId ?? 0),
      name: item?.Name ?? item?.name ?? '',
      keyword: item?.Keyword ?? item?.keyword ?? '',
      color: item?.Color ?? item?.color ?? '#46955A',
      count: Number(item?.Count ?? item?.count ?? 0),
    })),
});

const isSameDay = (a, b) => (
  a && b
  && a.day === b.day
  && a.month === b.month
  && a.year === b.year
);

const buildCalendarCells = (year, month, days) => {
  const dayMap = {};
  days.forEach((raw) => {
    const parsed = parseDay(raw);
    if (parsed.day > 0) dayMap[parsed.day] = parsed;
  });

  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push({ type: 'empty', key: `empty-${i}` });
  }

  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({
      type: 'day',
      key: `day-${d}`,
      data: dayMap[d] || {
        day: d,
        month,
        year,
        totalOrders: 0,
        statusCounts: [],
      },
    });
  }

  return cells;
};

const getDayName = (year, month, day, short = false) => {
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { weekday: short ? 'short' : 'long' });
};

const formatDayDate = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return `${day}/${month}/${year}`;
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

function DayDetailModal({ dayData, useDeliveryDate, onClose }) {
  if (!dayData) return null;

  const { day, month, year, totalOrders, statusCounts } = dayData;
  const dayName = getDayName(year, month, day);
  const maxCount = Math.max(...statusCounts.map((s) => s.count), 1);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-detail-title"
      >
        <div className="border-b border-gray-100 bg-gradient-to-r from-primary/10 via-white to-emerald-50/50 px-5 py-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">{dayName}</p>
              <h2 id="day-detail-title" className="mt-1 text-xl font-bold text-gray-900">
                {formatDayDate(year, month, day)}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                Based on {useDeliveryDate ? 'delivery date' : 'order created date'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 ring-1 ring-primary/20">
            <Package className="h-5 w-5 text-primary" />
            <span className="text-sm text-gray-600">Total orders</span>
            <span className="text-2xl font-bold text-primary">{totalOrders}</span>
          </div>
        </div>

        <div className="max-h-[min(60vh,420px)] overflow-y-auto px-5 py-4">
          {totalOrders === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No orders on this day.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status breakdown</p>
              {statusCounts.map((status) => {
                const pct = Math.round((status.count / maxCount) * 100);
                return (
                  <div
                    key={`${day}-${status.statusId}`}
                    className="rounded-xl border border-gray-100 bg-gray-50/80 p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: status.color || '#46955A' }}
                        />
                        <span className="truncate text-sm font-semibold text-gray-900">{status.name}</span>
                      </div>
                      <span
                        className="shrink-0 rounded-lg px-2.5 py-1 text-sm font-bold text-white"
                        style={{ backgroundColor: status.color || '#46955A' }}
                      >
                        {status.count}
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: status.color || '#46955A',
                        }}
                      />
                    </div>
                    <p className="mt-1 text-right text-[11px] text-gray-500">
                      {((status.count / totalOrders) * 100).toFixed(0)}% of day total
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 text-center">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-primary px-6 py-2 text-sm font-semibold text-white hover:bg-green-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DayCell({ dayData, isToday, isSelected, onSelect }) {
  const { day, month, year, totalOrders, statusCounts } = dayData;
  const topStatuses = statusCounts.slice(0, 3);
  const dayName = getDayName(year, month, day, true);
  const hasMore = statusCounts.length > 3;

  return (
    <button
      type="button"
      onClick={() => onSelect(dayData)}
      title={`${formatDayDate(year, month, day)} — click for full details`}
      className={`group flex min-h-[118px] flex-col rounded-xl border p-2 text-left transition-all sm:min-h-[132px] sm:p-3 ${
        isSelected
          ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/30'
          : isToday
            ? 'border-primary/40 bg-emerald-50/60 hover:border-primary/60 hover:shadow-md'
            : 'border-gray-200 bg-white hover:border-primary/30 hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
            {dayName}
          </p>
          <span className={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-lg text-sm font-bold ${
            isToday ? 'bg-primary text-white' : 'text-gray-800'
          }`}
          >
            {day}
          </span>
        </div>
        {totalOrders > 0 ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
            {totalOrders}
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex flex-1 flex-col justify-end gap-1.5">
        {totalOrders === 0 ? (
          <span className="text-[11px] text-gray-400">No orders</span>
        ) : (
          topStatuses.map((status) => (
            <div key={`${day}-${status.statusId}`} className="flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: status.color || '#46955A' }}
              />
              <span className="truncate text-[10px] font-medium text-gray-600 sm:text-[11px]" title={`${status.name}: ${status.count}`}>
                {status.count} {status.name}
              </span>
            </div>
          ))
        )}
        {hasMore ? (
          <span className="text-[10px] font-semibold text-primary group-hover:underline">
            +{statusCounts.length - 3} more · tap to view
          </span>
        ) : totalOrders > 0 ? (
          <span className="text-[10px] font-medium text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">
            Tap for details
          </span>
        ) : null}
      </div>
    </button>
  );
}
const OrderCalendar = () => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [useDeliveryDate, setUseDeliveryDate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthData, setMonthData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [detailDay, setDetailDay] = useState(null);
  const [printing, setPrinting] = useState(false);
  const loadSeq = useRef(0);

  const handleDaySelect = (dayData) => {
    setSelectedDay(dayData);
    setDetailDay(dayData);
  };
  const loadMonth = useCallback(async (month, year, dateFieldDelivery) => {
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetOrderCalendarMonth({
        Month: month,
        Year: year,
        UseDeliveryDate: dateFieldDelivery,
      });
      if (seq !== loadSeq.current) return;

      const parsed = parseMonthResponse(response);
      const days = parsed.days.map(parseDay);
      setMonthData({ ...parsed, days });
      setSelectedDay((prev) => {
        if (!prev) return prev;
        const match = days.find((d) => isSameDay(d, prev));
        return match || null;
      });
    } catch (err) {
      if (seq !== loadSeq.current) return;
      console.error('Load order calendar month error:', err);
      setError('Failed to load calendar data. Please try again.');
      setMonthData(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonth(viewMonth, viewYear, useDeliveryDate);
  }, [loadMonth, viewMonth, viewYear, useDeliveryDate]);

  const days = monthData?.days ?? [];
  const monthTotal = monthData?.monthTotal ?? 0;
  const cells = useMemo(() => buildCalendarCells(viewYear, viewMonth, days), [viewYear, viewMonth, days]);

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    setViewMonth(today.getMonth() + 1);
    setViewYear(today.getFullYear());
    setSelectedDay({
      day: today.getDate(),
      month: today.getMonth() + 1,
      year: today.getFullYear(),
      totalOrders: 0,
      statusCounts: [],
    });
  };

  const monthStatusSummary = useMemo(() => {
    const map = new Map();
    days.forEach((day) => {
      day.statusCounts.forEach((status) => {
        const key = status.statusId || status.name;
        const existing = map.get(key);
        if (existing) {
          existing.count += status.count;
        } else {
          map.set(key, { ...status });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [days]);

  const isTodayCell = (dayData) => (
    dayData.day === today.getDate()
    && dayData.month === today.getMonth() + 1
    && dayData.year === today.getFullYear()
  );

  const printCalendar = async () => {
    if (!days.length) {
      setError('No calendar data to print');
      return;
    }

    try {
      setPrinting(true);
      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (err) {
        console.warn('Order calendar print: no print header', err);
      }

      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header">${printHeaderHtml}</div>`
        : '';

      const daysWithOrders = days.filter((d) => d.totalOrders > 0);
      const dateFieldLabel = useDeliveryDate ? 'Delivery date' : 'Order created date';

      const dayRowsHtml = days.map((day) => {
        const dayName = getDayName(day.year, day.month, day.day);
        const statusText = day.statusCounts.length
          ? day.statusCounts.map((s) => `${s.count} ${s.name}`).join(', ')
          : '—';
        return `
          <tr>
            <td>${escapeHtml(dayName)}</td>
            <td>${day.day}/${day.month}/${day.year}</td>
            <td class="num">${day.totalOrders}</td>
            <td>${escapeHtml(statusText)}</td>
          </tr>
        `;
      }).join('');

      const summaryRowsHtml = monthStatusSummary.map((status) => `
        <tr>
          <td>${escapeHtml(status.name)}</td>
          <td class="num">${status.count}</td>
        </tr>
      `).join('');

      const htmlDoc = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8"/>
            <title>&#x200b;</title>
            <style>
              html, body { margin: 0; padding: 0; }
              body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; }
              .print-inner { box-sizing: border-box; padding: 14mm 16mm 16mm 16mm; }
              .company-print-header { margin-bottom: 14px; }
              .company-print-header img { max-height: 72px; }
              .header { border-bottom: 2px solid #d1d5db; padding-bottom: 14px; margin-bottom: 18px; }
              h1 { margin: 0; font-size: 22px; }
              .meta { margin-top: 6px; color: #4b5563; font-size: 12px; }
              h2 { margin: 20px 0 10px; font-size: 14px; }
              table { width: 100%; border-collapse: collapse; margin-top: 8px; }
              th, td { border: 1px solid #d1d5db; padding: 8px 10px; text-align: left; vertical-align: top; }
              th { background: #f3f4f6 !important; font-size: 10px; text-transform: uppercase; color: #4b5563; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              td.num { text-align: right; font-weight: 700; }
              .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 12px; }
              .summary-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; }
              .summary-box .label { font-size: 10px; color: #6b7280; text-transform: uppercase; }
              .summary-box .value { font-size: 18px; font-weight: 700; margin-top: 4px; }
              @media print {
                @page { size: A4 landscape; margin: 12mm 14mm 14mm 14mm; }
                html, body { margin: 0 !important; padding: 0 !important; }
                .print-inner { padding: 0 !important; }
                thead { display: table-header-group; }
                tr { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="print-inner">
              ${headerBlock}
              <div class="header">
                <h1>Orders Calendar — ${escapeHtml(MONTH_NAMES[viewMonth - 1])} ${viewYear}</h1>
                <div class="meta"><strong>Date field:</strong> ${escapeHtml(dateFieldLabel)}</div>
                <div class="meta"><strong>Printed:</strong> ${escapeHtml(new Date().toLocaleString())}</div>
              </div>
              <div class="summary-grid">
                <div class="summary-box"><div class="label">Month total</div><div class="value">${monthTotal}</div></div>
                <div class="summary-box"><div class="label">Days with orders</div><div class="value">${daysWithOrders.length}</div></div>
                <div class="summary-box"><div class="label">Days in month</div><div class="value">${days.length}</div></div>
              </div>
              <h2>Daily breakdown</h2>
              <table>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Date</th>
                    <th style="text-align:right;">Orders</th>
                    <th>Status summary</th>
                  </tr>
                </thead>
                <tbody>${dayRowsHtml}</tbody>
              </table>
              ${monthStatusSummary.length ? `
                <h2>Month status summary</h2>
                <table style="max-width:400px;">
                  <thead>
                    <tr><th>Status</th><th style="text-align:right;">Count</th></tr>
                  </thead>
                  <tbody>${summaryRowsHtml}</tbody>
                </table>
              ` : ''}
            </div>
          </body>
        </html>
      `;

      await printHtmlInSameTab(htmlDoc, 'Orders calendar print');
    } catch (err) {
      console.error('Order calendar print error:', err);
      setError('Failed to print calendar');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-primary/10 p-3 text-primary">
              <CalendarDays className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Orders Calendar</h1>
              <p className="mt-1 text-sm text-gray-500">
                Daily order counts with status breakdown. Change month to load that period.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={useDeliveryDate}
                onChange={(e) => setUseDeliveryDate(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              Use delivery date
            </label>
            <button
              type="button"
              onClick={goToday}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Today
            </button>
            <button
              type="button"
              onClick={printCalendar}
              disabled={loading || printing || days.length === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              <Printer className={`h-4 w-4 ${printing ? 'animate-pulse' : ''}`} />
              Print
            </button>
            <button
              type="button"
              onClick={() => loadMonth(viewMonth, viewYear, useDeliveryDate)}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Month total</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{loading ? '—' : monthTotal}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Days with orders</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {loading ? '—' : days.filter((d) => d.totalOrders > 0).length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Date field</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            {useDeliveryDate ? 'Delivery date' : 'Order created date'}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrevMonth}
              className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
              title="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="min-w-[180px] text-center text-lg font-bold text-gray-900">
              {MONTH_NAMES[viewMonth - 1]} {viewYear}
            </h2>
            <button
              type="button"
              onClick={goNextMonth}
              className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
              title="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          {loading ? (
            <span className="inline-flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading…
            </span>
          ) : null}
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-200 p-px">
          {WEEKDAYS.map((label) => (
            <div
              key={label}
              className="bg-gray-50 px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {label}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2 bg-gray-50 p-3 sm:gap-3 sm:p-4">
          {cells.map((cell) => (
            cell.type === 'empty' ? (
              <div key={cell.key} className="min-h-[108px] rounded-xl border border-transparent sm:min-h-[120px]" />
            ) : (
              <DayCell
                key={cell.key}
                dayData={cell.data}
                isToday={isTodayCell(cell.data)}
                isSelected={isSameDay(selectedDay, cell.data)}
                onSelect={handleDaySelect}
              />            )
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Package className="h-5 w-5 text-primary" />
              Month status summary
            </h3>
            <p className="mt-1 text-sm text-gray-500">Total orders grouped by status for this month.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                Loading…
              </div>
            ) : monthStatusSummary.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">No orders in this month.</p>
            ) : (
              monthStatusSummary.map((status) => (
                <div key={status.statusId || status.name} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: status.color || '#46955A' }}
                    />
                    <span className="truncate text-sm font-medium text-gray-900">{status.name}</span>
                  </div>
                  <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-sm font-bold text-gray-800">
                    {status.count}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Selected day</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedDay
                ? formatDayDate(selectedDay.year, selectedDay.month, selectedDay.day)
                : 'Click a day on the calendar to see status breakdown.'}
            </p>          </div>
          <div className="divide-y divide-gray-100">
            {!selectedDay ? (
              <p className="py-12 text-center text-sm text-gray-500">No day selected.</p>
            ) : selectedDay.totalOrders === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">No orders on this day.</p>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <span className="text-sm font-medium text-gray-600">Total orders</span>
                  <span className="text-xl font-bold text-primary">{selectedDay.totalOrders}</span>
                </div>
                {selectedDay.statusCounts.map((status) => (
                  <div key={`${selectedDay.day}-${status.statusId}`} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: status.color || '#46955A' }}
                      />
                      <span className="truncate text-sm font-medium text-gray-900">{status.name}</span>
                    </div>
                    <span className="rounded-lg bg-primary/10 px-2.5 py-1 text-sm font-bold text-primary">
                      {status.count}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <DayDetailModal
        dayData={detailDay}
        useDeliveryDate={useDeliveryDate}
        onClose={() => setDetailDay(null)}
      />
    </div>  );
};

export default OrderCalendar;
